/**
 * Edit-loan only: rebuild Application Progress the same way Create does —
 * by running each section's validation against saved loan data.
 * Create / New Loan do not import this module.
 */
import { preValidateXmlSectionsFromData,} from "./pre-validate-xml-sections";
import { CREDIT_REISSUE_CONSTANTS } from "./credit-reissue-constants";


const EDIT_LOAN_STEPS = [
	"Borrower Information",
	"Address",
	"Credit Reissue",
	"Employment and Icome",
	"Assets and Liabilities",
	"Declarations and Others",
	"Loan Product and Terms",
	"Underwriting Summary",
	"Loan Pricing",
	"Closing Information",
	"Supporting Documnets",
]

const EDIT_VISIT_ONCE_STEPS = ["Assets and Liabilities","Declarations and Others",]

const getStepNavIndex = (stepname)=> EDIT_LOAN_STEPS.indexOf(stepname);

const EDIT_VISIT_ONCE_NAV_INDICES = new Set(
	EDIT_VISIT_ONCE_STEPS.map(getStepNavIndex).filter((index)=>index>=0)
);
/** Fields needed to mirror Terms / Fees validateSection (Create hydrate fields) */
export const EDIT_LOAN_PROGRESS_FIELDS =
	"1109,3,4,19,2150,136,356,1821,353,FE0115,1548,1947,2982,MORNET.X15,VASUMM.X23,ULDD.TotalMortgagedPropertiesCount,CX.HOUSINGEVENTTYPE,CX.HOUSINGEVENTSEASONING,CX.BANKRUPTCYTYPE,CX.BANKRUPTCYOUTCOME,CX.BANKRUPTCYSEASONING,CX.MTGLATE.ANY,1041,ULDD.X140,ULDD.X177,URLA.X207,URLA.X208,CX.PROPERTY.COMMERCIAL.TYPE,CX.PROPERTY.COMMERCIAL.UNITS,608,16,14,15,13,LCP.X1,CX.INCOMEVERIFYTYPE,3245,33,2966,CX.PORTAL.CORPTRUSTORGTYP,CX.PORTAL.FINAL.VESTING,CX.PORTAL.CORPTRUSTNAME,CX.CPL.USINGPOA.BORROWER,CX.CPL.USINGPOA.COBORROWER,4004,1811,19";

const findField = (fieldValues, fieldId) =>
	fieldValues?.find((item) => String(item.fieldId) === String(fieldId))
		?.value;

const hasValue = (value) => {
	if (value === undefined || value === null) {
		return false;
	}

	const normalized = String(value).trim();

	return normalized !== "" && normalized !== "0.00";
};

/**
 * Same property type / design derivation as create-loan-terms updateField().
 */
const derivePropertyTypeAndDesign = (fieldValues) => {
	const propertyTypeValue = findField(fieldValues, "1041");
	const attachmentTypeValue = findField(fieldValues, "ULDD.X177");
	const projectDesignType = findField(fieldValues, "ULDD.X140");
	const propertyCommercialTypeValue = findField(
		fieldValues,
		"CX.PROPERTY.COMMERCIAL.TYPE"
	);
	const fieldUnitCount = findField(fieldValues, "16");
	const customFieldUnitCount = findField(
		fieldValues,
		"CX.PROPERTY.COMMERCIAL.UNITS"
	);
	const isPUD = findField(fieldValues, "URLA.X207") === "Y";
	const isPropertyNotLocatedInProject =
		findField(fieldValues, "URLA.X208") === "Y";

	const designFromAttachment = (attachment) =>
		projectDesignType ||
		(attachment === "Attached" ? "Townhouse Rowhouse" : attachment);

	if (propertyTypeValue === "HighRiseCondominium") {
		return {
			propertyType: "HighRiseCondominium",
			propertyDesign: projectDesignType || attachmentTypeValue,
			unitCount: fieldUnitCount,
		};
	}

	if (propertyTypeValue === "Condominium") {
		return {
			propertyType: "Condominium",
			propertyDesign: projectDesignType || attachmentTypeValue,
			unitCount: fieldUnitCount,
		};
	}

	if (isPUD && propertyTypeValue === "PUD") {
		return {
			propertyType: "Planned Unit Development",
			propertyDesign: designFromAttachment(attachmentTypeValue),
			unitCount: fieldUnitCount,
		};
	}

	if (
		isPropertyNotLocatedInProject &&
		Number(fieldUnitCount) === 4 &&
		propertyCommercialTypeValue === "Residential 5 - 8"
	) {
		return {
			propertyType: "5-8 Unit Residential",
			propertyDesign: designFromAttachment(attachmentTypeValue),
			unitCount: customFieldUnitCount,
		};
	}

	if (
		isPropertyNotLocatedInProject &&
		Number(fieldUnitCount) === 4 &&
		propertyCommercialTypeValue === "Mixed Use Residential"
	) {
		return {
			propertyType: "5-8 Unit Mixed Use",
			propertyDesign: designFromAttachment(attachmentTypeValue),
			unitCount: customFieldUnitCount,
		};
	}

	if (isPropertyNotLocatedInProject && Number(fieldUnitCount) > 1) {
		return {
			propertyType: "2-4 Unit Property",
			propertyDesign: designFromAttachment(attachmentTypeValue),
			unitCount: fieldUnitCount,
		};
	}

	if (isPropertyNotLocatedInProject && Number(fieldUnitCount) === 1) {
		return {
			propertyType: "Single Family",
			propertyDesign: designFromAttachment(attachmentTypeValue),
			unitCount: fieldUnitCount,
		};
	}

	return {
		propertyType: propertyTypeValue,
		propertyDesign: projectDesignType || attachmentTypeValue,
		unitCount: fieldUnitCount,
	};
};

/**
 * Mirrors create-loan-terms: hydrate from fields (same defaults) then validateSection.
 */
const validateTermsSectionFromFields = (fieldValues) => {
	if (!fieldValues?.length) {
		return false;
	}

	const documentType = findField(fieldValues, "CX.INCOMEVERIFYTYPE");
	const requestedInterestRate = findField(fieldValues, "3");
	// Create default: setLockTerm(... || 30)
	const lockTerm = findField(fieldValues, "2150") || 30;
	const loanPurpose = findField(fieldValues, "19");
	const purchasePrice = findField(fieldValues, "136");
	const estimatedValue =
		findField(fieldValues, "356") || findField(fieldValues, "1821");
	const loanAmount = findField(fieldValues, "1109");
	const loanValue = findField(fieldValues, "353");
	const employmentStatus = findField(fieldValues, "FE0115");
	const reservesDuration = findField(fieldValues, "1548");
	const prepaymentMonths = findField(fieldValues, "1947");
	const interestOnly = findField(fieldValues, "2982");
	// Create default: escrow || "N"
	const escrow = findField(fieldValues, "MORNET.X15") || "N";
	const creditScore = findField(fieldValues, "VASUMM.X23");
	const financedProperties = findField(
		fieldValues,
		"ULDD.TotalMortgagedPropertiesCount"
	);
	const housingType = findField(fieldValues, "CX.HOUSINGEVENTTYPE");
	const housingSeasoning = findField(fieldValues, "CX.HOUSINGEVENTSEASONING");
	const bankruptcyType = findField(fieldValues, "CX.BANKRUPTCYTYPE");
	const bankruptcyOutcome = findField(fieldValues, "CX.BANKRUPTCYOUTCOME");
	const bankruptcySeasoning = findField(fieldValues, "CX.BANKRUPTCYSEASONING");
	const mortgageLates = findField(fieldValues, "CX.MTGLATE.ANY");
	const amortization = findField(fieldValues, "608");
	const state = findField(fieldValues, "14");
	const zipcode = findField(fieldValues, "15");
	const county = findField(fieldValues, "13");
	const compensationPlan = findField(fieldValues, "LCP.X1");
	const numberOfStories = findField(fieldValues, "3245");

	const { propertyType, propertyDesign, unitCount } =
		derivePropertyTypeAndDesign(fieldValues);

	let paymentPenalty = "";
	if (
		prepaymentMonths !== undefined &&
		prepaymentMonths !== null &&
		prepaymentMonths !== ""
	) {
		paymentPenalty =
			prepaymentMonths === "0" || prepaymentMonths === 0
				? 0
				: Number(prepaymentMonths) / 12;
	}

	// Same checks as create-loan-terms validateSection
	if (
		documentType === undefined ||
		documentType === "" ||
		requestedInterestRate === undefined ||
		requestedInterestRate === "" ||
		Number(requestedInterestRate) === 0 ||
		lockTerm === undefined ||
		lockTerm === "" ||
		Number(lockTerm) === 0 ||
		loanPurpose === undefined ||
		loanPurpose === "" ||
		purchasePrice === undefined ||
		purchasePrice === "" ||
		purchasePrice === "0.00" ||
		estimatedValue === undefined ||
		estimatedValue === "" ||
		estimatedValue === "0.00" ||
		loanAmount === undefined ||
		loanAmount === "" ||
		loanAmount === "0.00" ||
		loanValue === undefined ||
		loanValue === "" ||
		employmentStatus === undefined ||
		employmentStatus === "" ||
		reservesDuration === undefined ||
		reservesDuration === "" ||
		paymentPenalty === "" ||
		Number.isNaN(Number(paymentPenalty)) ||
		interestOnly === undefined ||
		interestOnly === "" ||
		escrow === undefined ||
		escrow === "" ||
		creditScore === undefined ||
		creditScore === "" ||
		creditScore === 0 ||
		financedProperties === undefined ||
		financedProperties === "" ||
		financedProperties < 1 ||
		financedProperties > 99 ||
		housingType === undefined ||
		housingType === "" ||
		housingSeasoning === undefined ||
		housingSeasoning === "" ||
		bankruptcyType === undefined ||
		bankruptcyType === "" ||
		bankruptcyOutcome === undefined ||
		bankruptcyOutcome === "" ||
		bankruptcySeasoning === undefined ||
		bankruptcySeasoning === "" ||
		mortgageLates === null ||
		mortgageLates === undefined ||
		mortgageLates === "" ||
		propertyType === undefined ||
		propertyType === "" ||
		propertyDesign === undefined ||
		propertyDesign === "" ||
		amortization === undefined ||
		amortization === "" ||
		unitCount === undefined ||
		unitCount === "" ||
		unitCount === "0" ||
		unitCount === 0 ||
		state === undefined ||
		state === "" ||
		zipcode === undefined ||
		zipcode === "" ||
		county === undefined ||
		county === "" ||
		compensationPlan === undefined ||
		compensationPlan === ""
	) {
		return false;
	}

	if (
		propertyType === "HighRiseCondominium" ||
		propertyType === "Condominium" ||
		propertyType === "Condotel"
	) {
		if (Number(numberOfStories) < 1 || Number(numberOfStories) > 50) {
			return false;
		}
	}

	return true;
};

/**
 * Mirrors create-loan-fee validateFeeSection core checks (vesting + POA).
 * Create saves vesting on CX.PORTAL.CORPTRUSTORGTYP or field 33 (Sole Ownership, etc.).
 */
const validateFeesSectionFromFields = (fieldValues) => {
	if (!fieldValues?.length) {
		return false;
	}

	const vestingMethod =
		findField(fieldValues, "CX.PORTAL.CORPTRUSTORGTYP") ||
		findField(fieldValues, "33");
	const borrowerPoa = findField(fieldValues, "CX.CPL.USINGPOA.BORROWER");
	const coBorrowerName = findField(fieldValues, "4004");
	const coBorrowerPoa = findField(fieldValues, "CX.CPL.USINGPOA.COBORROWER");

	if (!hasValue(vestingMethod) || !hasValue(borrowerPoa)) {
		return false;
	}

	// Same as Create: co-borrower POA required when co-borrower name exists
	if (hasValue(coBorrowerName) && !hasValue(coBorrowerPoa)) {
		return false;
	}

	return true;
};

/**
 * Returns completed + incomplete nav indices (loanSteps 0–10) for Edit open,
 * using the same validation ideas as Create section validate handlers.
 */
export const getEditLoanSectionProgress = (
	applicationData,
	fieldValues = [],
	options = {}
) => {
	const completed = new Set();
	const incomplete = new Set();

	if (!applicationData?.applications?.length) {
		return { completed: [], incomplete: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10] };
	}

	// Borrower, Address, Credit, Declarations — from shared preValidate helpers.
	// Assets is excluded from Create XML preValidate (visit-once); on Edit restore,
	// mark Assets complete when saved data passes the same section validate as Create.
	const earlyCompleted = preValidateXmlSectionsFromData(
		applicationData,
		options
	);
	earlyCompleted.forEach((navIndex) => {
		if(EDIT_VISIT_ONCE_NAV_INDICES.has(navIndex)){
			return;
		}
		completed.add(navIndex);
	});

	const creditProvided =
		options.creditProvided || applicationData?.creditReissueData || "";

	if (creditProvided === CREDIT_REISSUE_CONSTANTS.MANUAL) {
		completed.add(2);
	}

	// // Assets (4) — same validateAssetsSection Create uses after Save and Continue
	// if (validateAssetsSection(applicationData)) {
	// 	completed.add(4);
	// }

	[0, 1, 2, 4, 5].forEach((navIndex) => {
		if (!completed.has(navIndex)) {
			incomplete.add(navIndex);
		}
	});

	// Employment (3) — Create validate is () => true; marked complete when
	// the section is reached (after Credit) the same as Create register/advance
	if (completed.has(2)) {
		completed.add(3);
	} else {
		incomplete.add(3);
	}

	// Loan Product and Terms (6) — Create hydrate defaults + validateSection
	if (validateTermsSectionFromFields(fieldValues)) {
		completed.add(6);
		incomplete.delete(6);
		// User reached Terms in Create/Edit only after Assets + Declarations —
		// retain those as complete when revisiting Edit (Save and Continue progress)
		completed.add(4);
		completed.add(5);
		incomplete.delete(4);
		incomplete.delete(5);
	} else {
		incomplete.add(6);
	}

	// Underwriting — Create validate is () => true after Terms
	if (completed.has(6)) {
		completed.add(7);
	} else {
		incomplete.add(7);
	}

	// Pricing (8) + Closing (9) — Closing complete when saved fields pass
	// the same core checks as Create validateFeeSection after Save and Continue.
	const feesComplete = validateFeesSectionFromFields(fieldValues);
	if (feesComplete) {
		completed.add(8);
		completed.add(9);
		incomplete.delete(8);
		incomplete.delete(9);
	} else {
		incomplete.add(8);
		incomplete.add(9);
	}

	incomplete.add(10);

	return {
		completed: [...completed].sort((a, b) => a - b),
		incomplete: [...incomplete].sort((a, b) => a - b),
	};
};
